import {
  adminResources,
  ControlClientError,
  createContextMutationPrecondition,
  parseContextSession,
  type ContextSessionResponse,
  type ControlWebClient,
  type MembershipRoleChoiceResponse,
  type OrganizationRoleResponse,
  type RelationshipStatus,
  type RequestOptions,
} from "@kontextwerk/control-web-client"
import { get } from "svelte/store"
import {
  captureControlContext,
  controlContext,
} from "@kontextwerk/control-web-frame/shared/logic/authentication"
import { getControlWebClient } from "../client"
import {
  invalid,
  membershipAccess,
  membershipAccessChange,
  uuid,
  type MembershipAccess,
  type MembershipAccessChange,
} from "./_logic/contracts"
export type { MembershipAccess, MembershipAccessChange } from "./_logic/contracts"

type Capture = Required<Pick<RequestOptions, "signal" | "isCurrent" | "mutationContext">>

export type MembershipAccessUpdate = Readonly<{
  role_id: string
  status: RelationshipStatus
  expected_revision: number
}>

/**
 * Membership, role and role-choice reads plus the single access write, all bound to one captured
 * KB organization context. The membership detail is read through a local contract because the
 * server may add the optional e-mail; writes are never replayed.
 */
export const createMembershipAccessApi = (
  client: ControlWebClient,
  value: ContextSessionResponse,
  capture: Capture,
) => {
  const session = parseContextSession(value)
  const pair = createContextMutationPrecondition(capture.mutationContext)

  if (
    session.product_issuer !== "kehrblatt-control" ||
    session.context.kind !== "organization" ||
    session.session_id !== pair.expected_session_id ||
    session.context_revision !== pair.expected_context_revision
  )
    return invalid()

  const organizationId = uuid(session.context.organization_id)
  const path = `/admin/organizations/${organizationId}`
  const directory = new adminResources.AdminIdentityAdministrationAPI(client.transport)
  const current = (signal: AbortSignal) => {
    if (capture.signal.aborted || signal.aborted || !capture.isCurrent())
      throw new DOMException("Obsolete membership access context", "AbortError")
  }
  const bind = (signal: AbortSignal): Capture => {
    current(signal)

    return {
      signal: AbortSignal.any([capture.signal, signal]),
      isCurrent: capture.isCurrent,
      mutationContext: pair,
    }
  }

  return {
    organizationId,
    async load(membershipId: string, signal: AbortSignal): Promise<MembershipAccess> {
      const member = uuid(membershipId)
      const result = membershipAccess(
        await client.transport.request<unknown>("GET", `${path}/memberships/${member}`, {
          ...bind(signal),
          retry: "never",
        }),
        organizationId,
      )
      current(signal)

      if (result.id !== member) return invalid()

      return result
    },
    async change(
      membershipId: string,
      payload: MembershipAccessUpdate,
      signal: AbortSignal,
    ): Promise<MembershipAccessChange> {
      const member = uuid(membershipId)

      if (
        (payload.status !== "active" && payload.status !== "suspended") ||
        !Number.isSafeInteger(payload.expected_revision) ||
        payload.expected_revision < 1
      )
        return invalid()

      const json = {
        role_id: uuid(payload.role_id),
        status: payload.status,
        expected_revision: payload.expected_revision,
      }
      const result = membershipAccessChange(
        await client.transport.request<unknown>("PATCH", `${path}/memberships/${member}`, {
          ...bind(signal),
          retry: "never",
          requireCsrf: true,
          json,
        }),
        organizationId,
        member,
      )
      current(signal)

      return result
    },
    async roleChoices(signal: AbortSignal): Promise<readonly MembershipRoleChoiceResponse[]> {
      const result = await directory.membershipRoleChoices(organizationId, bind(signal))
      current(signal)

      return result.items
    },
    async role(roleId: string, signal: AbortSignal): Promise<OrganizationRoleResponse> {
      const result = await directory.role(organizationId, uuid(roleId), bind(signal))
      current(signal)

      return result
    },
  }
}
export type MembershipAccessApi = ReturnType<typeof createMembershipAccessApi>

const detailText = (detail: unknown): string => {
  if (typeof detail === "string") return detail

  if (detail && typeof detail === "object" && "detail" in detail)
    return detailText((detail as { detail: unknown }).detail)

  return ""
}

/** The server rejected an access change against the current membership state or its rules. */
export const isIdentityChangeConflict = (error: unknown): boolean =>
  error instanceof ControlClientError &&
  error.statusCodeOrStatus === 409 &&
  (error.reasonCode === "identity_change_conflict" ||
    detailText(error.detail) === "identity_change_conflict")

/** One immutable confirmed KB organization context; a context change aborts its signal. */
export const captureMembershipAccess = (organizationId: string) => {
  const context = get(controlContext)

  if (
    !context ||
    context.session.product_issuer !== "kehrblatt-control" ||
    context.session.password_change_required ||
    context.session.context.kind !== "organization" ||
    context.organizationId?.toLowerCase() !== organizationId.toLowerCase()
  )
    throw new DOMException("Unconfirmed membership access context", "AbortError")

  const capture = captureControlContext()

  if (!capture) throw new DOMException("Unconfirmed membership access context", "AbortError")

  return {
    api: createMembershipAccessApi(getControlWebClient(), context.session, capture),
    session: context.session,
    signal: capture.signal,
    current: () => !capture.signal.aborted && capture.isCurrent(),
  }
}
export type CapturedMembershipAccess = ReturnType<typeof captureMembershipAccess>
